/**
 * Pure field builders for the JobPosting JSON-LD emitted by
 * `components/seo.tsx`. They live outside that file so they can be tested
 * directly: worker-app applies `jotai/babel/plugin-react-refresh`, which leaves
 * component files unusable from a jest spec.
 */

/**
 * The states in which a job position is still online, and its page still
 * indexable. Same list as `openJpKeys` in `@restworld/utils-common`, kept local
 * because importing that barrel pulls in component modules that a worker-app
 * spec cannot load (see the note at the top of this file).
 */
const ONLINE_STATUS_KEYS = ['jp_screening', 'jp_shortlist_formation', 'jp_shortlist_sent'];

/**
 * Whether the job position is still online. A listing in shortlist formation or
 * with the shortlist already sent is online exactly like one in screening: the
 * page is served, indexable, and open to applications.
 */
export function isJobPositionOnline(statusKey?: string | null): boolean {
  return !!statusKey && ONLINE_STATUS_KEYS.includes(statusKey);
}

/**
 * The date after which the listing is no longer valid, or `undefined` when
 * there is no such date.
 *
 * "Online" is every state in `ONLINE_STATUS_KEYS`, not only `jp_screening`: on
 * 30/07/2026 there were 24 listings in `jp_shortlist_sent` with no
 * `went_offline_at`, and treating them as offline handed them the very expiry in
 * the past this function exists to remove.
 *
 * An online job position has no expiry: it stays online until the employer
 * closes it, or until PPU days run out on a date this page cannot know. This
 * used to emit `first_published_at + 60 days`, so every listing online for
 * longer than 60 days advertised an expiry already in the past and asked Google
 * to de-index a live ad. On 30/07/2026 that was 7 of the 129 online listings.
 *
 * Omitting the property is what Google documents for a posting with no known
 * expiry, and it is the only option that cannot go stale: a rolling
 * `now + N days` would still be served from the past by an ISR page nobody has
 * requested for a while, because regeneration happens on request and not on a
 * timer.
 *
 * An offline job position keeps its real expiry, `went_offline_at`, which is
 * what makes Google for Jobs drop the listing. When that date is missing, which
 * is the case for listings closed before `went_offline_at` existed, the
 * publication date stands in: it is not the real expiry, but it is certainly in
 * the past, and an offline listing with no expiry at all would keep asking to be
 * indexed.
 */
export function computeValidThrough({
  statusKey,
  wentOfflineAt,
  publishedAt,
}: {
  statusKey?: string | null;
  wentOfflineAt?: string | null;
  publishedAt?: string | null;
}): string | undefined {
  const isOffline = !!statusKey && !isJobPositionOnline(statusKey);
  if (!isOffline) return undefined;
  return firstValidDate(wentOfflineAt, publishedAt);
}

// A malformed timestamp would otherwise be published verbatim as a date.
function firstValidDate(...candidates: (string | null | undefined)[]): string | undefined {
  return candidates.find((value) => !!value && !Number.isNaN(new Date(value).getTime())) ?? undefined;
}

// Maps the free-text `restaurant.country` (stored inconsistently in IT or EN)
// to an ISO 3166-1 alpha-2 code for the JobPosting `addressCountry`. Covers
// every spelling present on the online listings, checked on production data on
// 30/07/2026: 13 distinct spellings for about ten countries.
const COUNTRY_NAME_TO_ISO: Record<string, string> = {
  italia: 'IT',
  italy: 'IT',
  germania: 'DE',
  germany: 'DE',
  spagna: 'ES',
  spain: 'ES',
  francia: 'FR',
  france: 'FR',
  svizzera: 'CH',
  switzerland: 'CH',
  austria: 'AT',
  'regno unito': 'GB',
  'united kingdom': 'GB',
  malta: 'MT',
  belgio: 'BE',
  belgium: 'BE',
  irlanda: 'IE',
  ireland: 'IE',
  portogallo: 'PT',
  portugal: 'PT',
  grecia: 'GR',
  greece: 'GR',
  brasile: 'BR',
  brazil: 'BR',
  messico: 'MX',
  mexico: 'MX',
  svezia: 'SE',
  sweden: 'SE',
  'paesi bassi': 'NL',
  olanda: 'NL',
  netherlands: 'NL',
  danimarca: 'DK',
  denmark: 'DK',
  cipro: 'CY',
  cyprus: 'CY',
  cina: 'CN',
  china: 'CN',
};

// The assigned ISO 3166-1 alpha-2 codes, so a value that already arrives
// normalised is kept whether or not its name is in the map above, while a
// user-assigned or invented pair such as "ZZ" is not.
const ISO_3166_ALPHA2 = new Set(
  `AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ
   CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO
   FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE
   JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO
   MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW
   PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM
   TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW`.split(/\s+/)
);

/**
 * The ISO 3166-1 alpha-2 code for a free-text country name, or `undefined` when
 * the name is missing or unknown.
 *
 * Returning `undefined` rather than the raw name is deliberate: the field is
 * then omitted from the JSON-LD. A country name where Google expects a code is
 * invalid data either way, and the empty string the previous version emitted
 * for a null country was invalid too.
 */
export function toCountryCode(country?: string | null): string | undefined {
  const raw = country?.trim();
  if (!raw) return undefined;

  const mapped = COUNTRY_NAME_TO_ISO[raw.toLowerCase()];
  if (mapped) return mapped;

  // Some records already carry a normalised code. Accept it when it is a real
  // alpha-2 code, so a stray two-letter value is dropped instead of published as
  // a country.
  const upper = raw.toUpperCase();
  return ISO_3166_ALPHA2.has(upper) ? upper : undefined;
}
